import {SegmentedControl} from '@dropbox/dig-components/controls';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {Box} from '@dropbox/dig-foundations';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {pulseUserAtom} from 'atoms/auth';
import type {Timeframe, WorkstreamBasicWithSummary, WorkstreamNavNode} from 'client';
import {Layout} from 'components/DSYS/Layout';
import {DSYS_RAIL_INSET_LEFT_PX, DSYS_RAIL_INSET_RIGHT_PX} from 'components/DSYS/layoutConstants';
import {Link} from 'components/DSYS/Link';
import {SkeletonFadeIn} from 'components/DSYS/SkeletonFadeIn';
import {useMobile} from 'hooks/isMobile';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useEmployee} from 'hooks/useEmployee';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {useEffect, useMemo, useRef, useState} from 'react';
import {
  useLocation,
  useNavigate,
  useNavigationType,
  useParams,
  useSearchParams,
} from 'react-router';
import {
  usePillarConfigs,
  usePillars,
  useTimeframesQuery,
  useWorkstreams,
} from 'views/workstreams/detail/hooks';
import {
  getDefaultMetricsTimeframe,
  type MetricsTimeframe,
  parseMetricsTimeframeParam,
  toMetricsTimeframeParam,
} from 'views/workstreams/detail/metricsTimeframe';
import {isSameLdap} from 'views/workstreams/detail/scopeRouting';
import type {
  AltitudeFilterValue,
  StatusValue,
  WorkstreamFilters,
} from 'views/workstreams/detail/types';
import {useMetricsScorecardControls} from 'views/workstreams/detail/useMetricsScorecardControls';
import {WorkstreamMetricsScorecardList} from 'views/workstreams/detail/WorkstreamMetricsScorecardList';
import {
  formatWorkstreamTimeframeLabel,
  getDefaultWorkstreamTimeframe,
  narrativePlanningHalfToTimeframeTableFilter,
  parseWorkstreamTimeframe,
  WorkstreamTimeframe,
} from 'views/workstreams/detail/workstreamTimeframe';
import {useWorkstream} from 'views/workstreams/hooks';
import {
  getStatusPriority,
  isRestrictedWorkstreamAccessResponse,
  isWorkstreamStatusArchived,
} from 'views/workstreams/utils';
import {WorkstreamStatusUpdateModal} from 'views/workstreams/WorkstreamStatusUpdateModal';

import {AuditPane, AuditScope, AuditScopeSwitch} from './AuditPane';
import {AuditRailRow} from './AuditRailRow';
import {EmbeddedRoadmap, RoadmapTarget} from './EmbeddedRoadmap';
import {EmbeddedScorecards} from './EmbeddedScorecards';
import {InboxPane} from './InboxPane';
import {InboxRailRow} from './InboxRailRow';
import {saveLandingSnapshot, useLandingSnapshot} from './landingSnapshot';
import {translateLegacyWorkstreamsLink} from './legacyWorkstreamsLinks';
import {MobileNavSheet, SHEET_BLEED} from './MobileNavSheet';
import {MyEmptyIntro} from './MyEmptyIntro';
import {groupByMyRole, personRole} from './myRoleGroups';
import {NarrativeLinkIcon} from './NarrativeLinkIcon';
import {NavTreeSkeleton} from './NavTreeSkeleton';
import {NewWorkstreamButton} from './NewWorkstreamButton';
import {OUTCOMES_OVERDUE_LABEL, splitOutcomesOverdue} from './outcomesOverdue';
import {ParentWorkstreamView} from './ParentWorkstreamView';
import {PersonScopeHeader, PersonScopeHeaderSkeleton} from './PersonScopeHeader';
import {AlertCountBadge} from './RailBadge';
import {
  isSameRecent,
  pickVisibleRecents,
  recordVisit,
  togglePin,
  withWorkstreamNames,
} from './recentsFrecency';
import {MAX_UNPINNED_RECENTS, useStoredRecents} from './recentsStorage';
import {RecentsStrip} from './RecentsStrip';
import {pickRecentItems, RecentStripItem} from './recentsStripData';
import {readScanCardOrigin} from './scanCardOrigin';
import {getCoreWorkstreamIds, ScanCoreWorkstreamsContext} from './scanCoreWorkstreams';
import {
  buildPillarGroupedRoots,
  ENABLEMENT_GROUP_DESCRIPTION,
  ENABLEMENT_GROUP_LABEL,
  getPillarLeads,
  mergePillarLeads,
  sortByRailOrder,
} from './treeData';
import {navNodeToWsNode, TreeItem, WsNode} from './treeTypes';
import {splitUpdateDue, UPDATE_DUE_LABEL} from './updateDue';
import {useMyWorkstreamsBadgeCount} from './useMyUpdatesDue';
import {useNavTree} from './useNavTree';
import {useNearestTeamWorkstreams} from './useNearestTeamWorkstreams';
import {useCanViewWireframes} from './wireframes/useCanViewWireframes';
import {WireframesPane} from './wireframes/WireframesPane';
import {WorkstreamDetailView} from './WorkstreamDetailView';
import {WorkstreamFocus} from './WorkstreamFocus';
import {WorkstreamListTable} from './WorkstreamListTable';
import {
  logWorkstreamsInteraction,
  logWorkstreamsViewed,
  readVia,
  readViaOrigin,
  UpdateOpenedFrom,
  viaState,
  WorkstreamsVia,
  WorkstreamsViaOrigin,
  WorkstreamsViewPlace,
} from './workstreamsAnalytics';
import {ScanSkeletonShape, WorkstreamScanList} from './WorkstreamScanList';
import {EmptyState, LoadError, TimeframeDropdown} from './WorkstreamsControls';
import {
  parseDetailLink,
  parsePersonLink,
  parseWorkstreamsPlace,
  WorkstreamPage,
  workstreamsPath,
  workstreamsPlacePath,
} from './workstreamsRoutes';
import {PinnedItem, WorkstreamsSearch} from './WorkstreamsSearch';
import {TreeSharedProps, WorkstreamTree} from './WorkstreamTree';

// Roadmap (and the scorecard on top of Metrics) are behind `?extra=1`,
// Wireframes for Finance admins (useCanViewWireframes). Audit and Inbox are
// rail places (their own paths) rather than views in the switch; `?exec=1`
// shows Inbox's rail row.
type Mode = 'execution' | 'metrics' | 'wireframes' | 'roadmap' | 'audit' | 'inbox';

// Modes that are rail places with their own pane, not a view of the rail's scope.
const isPlaceMode = (mode: Mode): mode is 'audit' | 'inbox' => mode === 'audit' || mode === 'inbox';

// The rail's rows bleed back across Layout.Page's rail insets, so fills run edge to edge.
const RAIL_BLEED = {left: DSYS_RAIL_INSET_LEFT_PX, right: DSYS_RAIL_INSET_RIGHT_PX};

// The rail's section dividers: a soft line, the same tint as the open group,
// bleeding edge to edge like the rows' fills, with 12px on both sides.
const RailDivider = ({bleed}: {bleed: {left: number; right: number}}) => (
  <Box
    marginTop="12"
    marginBottom="12"
    backgroundColor="Opacity Surface"
    style={{height: 1, marginLeft: -bleed.left, marginRight: -bleed.right}}
  />
);

const MY_GROUP_LABEL = 'My workstreams';
// The rail's Company row: the whole company, i.e. /workstreams/company.
// Its name is also the company narrative's key in the pillar configs.
const COMPANY_GROUP_LABEL = 'Company';
const COMPANY_GROUP_ID = -400;
// The rail's My workstreams row: flat, since its cards already group it by role.
const MY_PLACE_ID = -401;

// A rail row that opens a scope and has no rows of its own (so no chevron).
const placeRow = (id: number, name: string, badge?: number): TreeItem => ({
  node: {id, name, isPillarGroup: true},
  level: 1,
  badge,
  children: [],
});
const COMPANY_PLACE_ROOTS = [placeRow(COMPANY_GROUP_ID, COMPANY_GROUP_LABEL)];

// A place in the page. Mode and timeframe carry over unless set; the rest is
// the new scope (unset = cleared). A pillar is in Company.
type NavTarget = {
  mode?: Mode;
  half?: WorkstreamTimeframe;
  metricsTimeframe?: MetricsTimeframe;
  id?: number;
  page?: WorkstreamPage;
  company?: boolean;
  person?: string;
  pillar?: string;
};

const PINNED_BASE_ID = -2000;

// How a full page opens from within the page, with the place it opened from
// one history entry back.
const DETAIL_OPENED_IN_PAGE_VIAS: WorkstreamsVia[] = ['card_link', 'detail_page_link', 'create'];

const toWsNodes = (data?: WorkstreamNavNode[]): WsNode[] => (data ?? []).map(navNodeToWsNode);

// A personal card section: the rows sorted like the flat rail (health priority,
// then name), with archived ones split out for the list's one "Archived" fold.
// (Company cards keep the server's by-name order, the tree's.)
const toCardSection = (rows: WorkstreamBasicWithSummary[]) => ({
  workstreams: rows
    .filter((workstream) => !isWorkstreamStatusArchived(workstream))
    .sort(
      (a, b) =>
        getStatusPriority(a.updates?.[0]?.status) - getStatusPriority(b.updates?.[0]?.status) ||
        a.name.localeCompare(b.name)
    ),
  archived: rows.filter(isWorkstreamStatusArchived),
});

/**
 * /workstreams: a pared-down fork of /workstreams. One rail: My
 * workstreams (selected by default; hidden when you have none), the company's
 * pillars, then a search and a persisted recents list. No saved views, Planning
 * mode or page-level comments. `?table=1` lists them in WorkstreamListTable instead of cards.
 */
export const WorkstreamsPage = () => {
  // Every list here is scoped by a timeframe, so wait for them on a first
  // visit (nothing cached) rather than load the page for no timeframe and
  // then again. If they fail, the page still opens.
  const {timeframes, isLoading} = useTimeframesQuery();
  if (!timeframes.length && isLoading) {
    return (
      <Box display="flex" justifyContent="center" padding="24">
        <Spinner />
      </Box>
    );
  }
  return <WorkstreamsPageContent timeframes={timeframes} />;
};

const WorkstreamsPageContent = ({timeframes}: {timeframes: readonly Timeframe[]}) => {
  const {id: routeId, page: routePage} = useParams<{id?: string; page?: string}>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  // A link to the shipped /workstreams page (a scope, the old inbox, activity,
  // a comment) opens its place here instead. TEMPORARY, with its event: see
  // legacyWorkstreamsLinks for when to remove it.
  const legacyLink = translateLegacyWorkstreamsLink({
    id: routeId,
    page: routePage,
    search: location.search,
  });
  const legacyHref = legacyLink?.href;
  const legacyKinds = legacyLink?.kinds.join(',');
  useEffect(() => {
    if (!legacyHref || !legacyKinds) return;
    analyticsLogger().logEvent('WORKSTREAM_LEGACY_LINK_OPENED', {
      kinds: legacyKinds.split(','),
    });
    navigate(legacyHref, {replace: true});
  }, [legacyHref, legacyKinds, navigate]);

  // Where you are lives in the URL, so Back / Forward retrace rail clicks:
  //   /workstreams[/company|/audit|/inbox|/:id[/detail]]?view=…&person=…&pillar=…
  // :id alone focuses that workstream's card; /detail is its full page. The
  // timeframe is there too, when not the default.
  const showExtra = searchParams.get('extra') === '1';
  // The exec sponsor Inbox's rail row is behind `?exec=1` (/inbox opens without it).
  const showExec = searchParams.get('exec') === '1';
  // The scan list is the default; `?table=1` lists the same rows in a table.
  const showScanList = searchParams.get('table') !== '1';
  const canViewWireframes = useCanViewWireframes();
  const place = parseWorkstreamsPlace(routeId);
  // A view you can't open falls back to Details.
  const requestedView = searchParams.get('view');
  const mode: Mode =
    place === 'audit' || place === 'inbox'
      ? place
      : requestedView === 'metrics'
        ? 'metrics'
        : canViewWireframes && requestedView === 'wireframes'
          ? 'wireframes'
          : showExtra && requestedView === 'roadmap'
            ? 'roadmap'
            : 'execution';
  // One `timeframe` key, read by the open view: a half in Details and Wireframes
  // (which also scopes the workstreams loaded), a quarter or year in Metrics. Each
  // view reads only its own kind, so switching to the other starts on its default.
  const timeframeParam = searchParams.get('timeframe');
  const half = parseWorkstreamTimeframe(mode === 'metrics' ? null : timeframeParam, timeframes);
  const metricsTimeframe =
    (mode === 'metrics' ? parseMetricsTimeframeParam(timeframeParam) : null) ??
    getDefaultMetricsTimeframe();
  const selectedId = routeId && /^\d+$/.test(routeId) ? Number(routeId) : undefined;
  const page: WorkstreamPage = routePage === 'detail' ? 'detail' : 'focus';
  const {ldap: myLdap} = useAtomValue(pulseUserAtom) ?? {};
  // Your own ldap is My workstreams.
  const requestedPerson = searchParams.get('person')?.trim().toLowerCase() || undefined;
  const personParam =
    requestedPerson && !isSameLdap(requestedPerson, myLdap) ? requestedPerson : undefined;
  const pillarParam = searchParams.get('pillar') ?? undefined;
  const isCompanyPlace = place === 'company';
  // A person (searched / recent) scopes the list; otherwise it's My workstreams
  // unless a pillar (or the whole company) is picked.
  const personalGroup: 'mine' | string | null = personParam
    ? `person:${personParam}`
    : selectedId !== undefined || pillarParam || isCompanyPlace
      ? null
      : 'mine';
  // Whether the scope is Personal's or Company's: Personal only while My
  // workstreams is selected.
  const isPersonal = personalGroup === 'mine';
  const isCompany = !isPersonal;
  const selectedPillar =
    isCompany && selectedId === undefined && !personParam ? (pillarParam ?? null) : null;
  const companyOverviewOpen =
    isCompanyPlace && selectedId === undefined && !personParam && !pillarParam;

  const hrefFor = (target: NavTarget) => {
    const params = new URLSearchParams();
    // Audit and Inbox are rail rows, so picking anything else in the rail leaves them.
    const nextMode = target.mode ?? (isPlaceMode(mode) ? 'execution' : mode);
    const nextTimeframe =
      nextMode === 'metrics'
        ? toMetricsTimeframeParam(target.metricsTimeframe ?? metricsTimeframe)
        : (target.half ?? half);
    const defaultTimeframe =
      nextMode === 'metrics'
        ? toMetricsTimeframeParam(getDefaultMetricsTimeframe())
        : getDefaultWorkstreamTimeframe(new Date(), timeframes);
    const isPlace = isPlaceMode(nextMode);
    if (!isPlace && nextMode !== 'execution') params.set('view', nextMode);
    if (showExtra) params.set('extra', '1');
    if (showExec) params.set('exec', '1');
    if (!showScanList) params.set('table', '1');
    // Audit and Inbox have no scope of their own.
    if (!isPlace && target.person) params.set('person', target.person);
    if (!isPlace && target.pillar) params.set('pillar', target.pillar);
    if (nextTimeframe !== defaultTimeframe) params.set('timeframe', nextTimeframe);
    const path = isPlace
      ? workstreamsPlacePath(nextMode)
      : target.id !== undefined
        ? workstreamsPath(target.id, target.page)
        : !target.person && (target.company || target.pillar)
          ? workstreamsPlacePath('company')
          : workstreamsPath();
    const search = params.toString();
    return `${path}${search ? `?${search}` : ''}`;
  };
  const go = (
    target: NavTarget,
    {
      replace = false,
      via,
      origin,
    }: {replace?: boolean; via?: WorkstreamsVia; origin?: WorkstreamsViaOrigin} = {}
  ) => {
    const href = hrefFor(target);
    const isReplace = replace || href === `${location.pathname}${location.search}`;
    // Re-clicking what's already open doesn't stack a history entry. A replace
    // without a via of its own (a timeframe change) keeps the entry's, so a full
    // page still knows where it was opened from.
    navigate(href, {
      replace: isReplace,
      state: via ? viaState(via, origin) : isReplace ? location.state : undefined,
    });
  };
  // The back arrow's (and Esc's) step back, for the view event: Back / Forward
  // otherwise report 'history_or_load'.
  const pendingBackVia = useRef(false);
  // A full page opened from the cards, another full page or New workstream
  // steps back to wherever it was opened from, like the browser's Back (so My
  // workstreams → More details → back lands on My workstreams again). Opened
  // any other way (a link, a reload), there's no such entry to go back to, so
  // it closes to its card. `?table=1` has no cards: it closes to the table of
  // its pillar, else My workstreams.
  const closeDetail = (id: number) => {
    const openedVia = readVia(location.state);
    if (openedVia && DETAIL_OPENED_IN_PAGE_VIAS.includes(openedVia)) {
      pendingBackVia.current = true;
      navigate(-1);
    } else if (showScanList) {
      go({id}, {via: 'back'});
    } else {
      const pillar = allNavNodes.find((node) => node.id === id)?.pillar ?? undefined;
      go(pillar ? {pillar} : {}, {via: 'back'});
    }
  };
  // The current place, for changes that keep it (mode switches).
  const here: NavTarget = {
    mode,
    id: selectedId,
    page,
    company: isCompanyPlace,
    person: personParam,
    pillar: pillarParam,
  };
  // The timeframe is a setting, so changing it replaces the history entry
  // rather than adding one.
  const setHalf = (next: WorkstreamTimeframe) => go({...here, half: next}, {replace: true});
  const setMetricsTimeframe = (next: MetricsTimeframe) =>
    go({...here, metricsTimeframe: next}, {replace: true});

  const [updateId, setUpdateId] = useState<number | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<StatusValue[]>([]);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [recentItems, setRecentItems] = useStoredRecents();
  // The scoped person, for the pane's header and their recent.
  const {employee: scopedPerson, isLoading: scopedPersonLoading} = useEmployee({
    ldap: personParam,
    throwOnError: false,
  });
  const isMobile = useMobile();

  const tf = narrativePlanningHalfToTimeframeTableFilter(half, timeframes);

  // ---------------------------------------------------------------------------
  // Personal mode: My workstreams
  // ---------------------------------------------------------------------------
  const navFilters: WorkstreamFilters = {timeframes: [tf]};

  const myNavQuery = useNavTree({
    scope_type: 'my_workstreams',
    scope_value: null,
    filters: navFilters,
  });
  const myEnablementNavQuery = useNavTree({
    scope_type: 'my_workstreams',
    scope_value: null,
    filters: {timeframes: ['enablement']},
  });

  // Nothing active of yours in the half or Enablement (the trees leave out
  // archived ones). My workstreams then shows your nearest team's instead.
  // Only this half's real, loaded trees count: not last visit's or the last
  // half's stand-ins, nor a failed load, so someone with some never sees it.
  const myIsEmpty =
    [myNavQuery, myEnablementNavQuery].every(
      (query) => query.isSuccess && !query.isPlaceholderData
    ) && (myNavQuery.data?.length ?? 0) + (myEnablementNavQuery.data?.length ?? 0) === 0;

  // ---------------------------------------------------------------------------
  // Company mode
  // ---------------------------------------------------------------------------
  const companyNavQuery = useNavTree({
    scope_type: 'company',
    scope_value: null,
    filters: navFilters,
  });

  // Personal mode data for the right pane: My or a searched person, in
  // the selected half plus Enablement (off-narrative, so in every half), like
  // the rail.
  const isPersonGroup = personalGroup?.startsWith('person:') ?? false;
  const personalScopeType = isPersonGroup ? 'person' : 'my_workstreams';
  const personalScopeValue = isPersonGroup ? personalGroup!.slice('person:'.length) : null;
  const personalScopeEnabled = isPersonGroup ? !isPlaceMode(mode) : isPersonal;
  const personalFilters: WorkstreamFilters = useMemo(() => {
    const health = [...statusFilter, ...(includeArchived ? ['archived' as StatusValue] : [])];
    return {
      altitude: ['false'] as AltitudeFilterValue[],
      timeframes: [tf, 'enablement'],
      ...(health.length > 0 ? {health} : {}),
    };
  }, [tf, statusFilter, includeArchived]);
  // The table lists both together.
  const personalQuery = useWorkstreams({
    scope_type: personalScopeType,
    scope_value: personalScopeValue,
    filters: personalFilters,
    group_by: 'none',
    autoFetchAllPages: true,
    trackPerf: true,
    enabled: personalScopeEnabled && !showScanList,
  });
  // The cards list each in its own section, with every section's archived
  // workstreams in one collapsed "Archived" row below them all, so with no
  // status filter they load those too.
  const personalCardFilters = useMemo(
    () =>
      statusFilter.length === 0
        ? {...personalFilters, health: ['archived' as StatusValue]}
        : personalFilters,
    [personalFilters, statusFilter.length]
  );
  const personalHalfFilters: WorkstreamFilters = useMemo(
    () => ({...personalCardFilters, timeframes: [tf]}),
    [personalCardFilters, tf]
  );
  const personalEnablementFilters: WorkstreamFilters = useMemo(
    () => ({...personalCardFilters, timeframes: ['enablement']}),
    [personalCardFilters]
  );
  const personalHalfQuery = useWorkstreams({
    scope_type: personalScopeType,
    scope_value: personalScopeValue,
    filters: personalHalfFilters,
    group_by: 'none',
    autoFetchAllPages: true,
    trackPerf: true,
    enabled: personalScopeEnabled && showScanList,
  });
  const personalEnablementQuery = useWorkstreams({
    scope_type: personalScopeType,
    scope_value: personalScopeValue,
    filters: personalEnablementFilters,
    group_by: 'none',
    autoFetchAllPages: true,
    trackPerf: true,
    enabled: personalScopeEnabled && showScanList,
  });
  const nearestTimeframes = useMemo(() => [tf, 'enablement' as const], [tf]);
  const nearestTeam = useNearestTeamWorkstreams({
    timeframes: nearestTimeframes,
    health: statusFilter,
    enabled: isPersonal && personalGroup === 'mine' && showScanList && myIsEmpty,
  });

  // Company mode filters, optionally scoped to a selected pillar
  const companyFilters: WorkstreamFilters = useMemo(() => {
    const health = [...statusFilter, ...(includeArchived ? ['archived' as StatusValue] : [])];
    return {
      altitude: ['true'] as AltitudeFilterValue[],
      timeframes: [tf],
      ...(selectedPillar ? {pillar: [selectedPillar]} : {}),
      ...(health.length > 0 ? {health} : {}),
    };
  }, [tf, selectedPillar, statusFilter, includeArchived]);

  // An open workstream (its card or full page) shows only itself.
  const companyListShown = isCompany && selectedId === undefined;
  const companyWorkstreamsQuery = useWorkstreams({
    scope_type: 'company',
    scope_value: null,
    narrative_only: true,
    filters: companyFilters,
    group_by: 'none',
    autoFetchAllPages: true,
    trackPerf: true,
    enabled: companyListShown,
  });

  // ---------------------------------------------------------------------------
  // Build left-rail tree
  // ---------------------------------------------------------------------------
  // Last visit's rail and My cards (landingSnapshot), drawn until the live ones load.
  const landingSnapshot = useLandingSnapshot(myLdap, tf);
  const myNavData = myNavQuery.data ?? landingSnapshot?.navMy;
  const myEnablementNavData = myEnablementNavQuery.data ?? landingSnapshot?.navMyEnablement;
  const companyNavData = companyNavQuery.data ?? landingSnapshot?.navCompany;
  const myNodes = useMemo(() => toWsNodes(myNavData), [myNavData]);
  const myEnablementNodes = useMemo(() => toWsNodes(myEnablementNavData), [myEnablementNavData]);
  const companyNodes = useMemo(() => toWsNodes(companyNavData), [companyNavData]);

  const {data: livePillarOrder} = usePillars(half);
  const pillarOrder = livePillarOrder ?? landingSnapshot?.pillars;
  const {data: pillarConfigs} = usePillarConfigs(half);
  const liveNarrativeLinks = useMemo(() => {
    if (!pillarConfigs) return undefined;
    const map: Record<string, {text: string; url: string}> = {};
    for (const [name, config] of Object.entries(pillarConfigs)) {
      if (config.url) map[name] = {text: config.text, url: config.url};
    }
    return Object.keys(map).length > 0 ? map : undefined;
  }, [pillarConfigs]);
  const narrativeLinks = pillarConfigs ? liveNarrativeLinks : landingSnapshot?.narrativeLinks;

  const allNavNodes = useMemo(
    () => [...myNodes, ...myEnablementNodes, ...companyNodes],
    [myNodes, myEnablementNodes, companyNodes]
  );

  // Recents (people + workstreams) rendered below the search bar — one history
  // and one set of pins, identical in Personal and Company. Each is one flat
  // row; what's under it (sub-workstreams, a person's workstreams) shows in the
  // pane once opened. Workstreams reuse their nav node when loaded so the row
  // shows the real status. Person rows' ids carry their index in recentItems
  // (see recentForNode).
  const visibleRecentRoots = useMemo<TreeItem[]>(
    () =>
      pickVisibleRecents(recentItems, Date.now()).flatMap((item): TreeItem[] => {
        if (item.type === 'person') {
          return [
            {
              node: {
                id: PINNED_BASE_ID - recentItems.indexOf(item),
                name: item.name,
                person: {ldap: item.ldap, name: item.name, profile_photo_url: item.profilePhoto},
              },
              level: 1,
              children: [],
            },
          ];
        }
        if (item.type === 'workstream') {
          const node: WsNode = allNavNodes.find((n) => n.id === item.id) ?? {
            id: item.id,
            name: item.name,
          };
          return [{node, level: 1, children: []}];
        }
        return [];
      }),
    [recentItems, allNavNodes]
  );

  // Enablement work shows only under My workstreams, so Company drops its catch-all group.
  const companyRoots = useMemo(
    () =>
      buildPillarGroupedRoots(companyNodes, pillarOrder ?? []).filter(
        (root) => root.node.name !== ENABLEMENT_GROUP_LABEL
      ),
    [companyNodes, pillarOrder]
  );

  // Pillar workstreams + level 2: their cards flag a missing north star.
  const coreWorkstreamIds = useMemo(() => getCoreWorkstreamIds(companyRoots), [companyRoots]);
  // Pillars of one workstream (→ the pillar), and the rail with each folded into
  // its workstream. Everything else (the Company overview, Metrics' order) keeps
  // the plain tree.
  const pillarLeads = useMemo(() => getPillarLeads(companyRoots), [companyRoots]);
  const railCompanyRoots = useMemo(() => mergePillarLeads(companyRoots), [companyRoots]);

  // A recent/searched person scopes the list in either view.
  const isCompanyScope = isCompany && !isPersonGroup;

  const activeQuery = isCompanyScope ? companyWorkstreamsQuery : personalQuery;
  // Personal cards come in two lists (the half, then Enablement) instead.
  const personalCards = !isCompanyScope && showScanList;

  // The snapshot holds My's unfiltered cards only.
  const showsMyCards =
    personalCards && personalGroup === 'mine' && statusFilter.length === 0 && !includeArchived;
  const myCardsSnapshot = showsMyCards ? landingSnapshot : null;
  // Once My has all of it live, save it for the next visit.
  const liveHalfRows = personalHalfQuery.hasNextPage ? undefined : personalHalfQuery.data;
  const liveEnablementRows = personalEnablementQuery.hasNextPage
    ? undefined
    : personalEnablementQuery.data;
  useEffect(() => {
    if (!showsMyCards || !liveHalfRows || !liveEnablementRows) return;
    if (!myNavQuery.data || !myEnablementNavQuery.data || !companyNavQuery.data) return;
    if (!livePillarOrder || !pillarConfigs) return;
    saveLandingSnapshot(myLdap, {
      timeframe: tf,
      navMy: myNavQuery.data,
      navMyEnablement: myEnablementNavQuery.data,
      navCompany: companyNavQuery.data,
      pillars: livePillarOrder,
      narrativeLinks: liveNarrativeLinks,
      myHalf: liveHalfRows.workstreams as WorkstreamBasicWithSummary[],
      myEnablement: liveEnablementRows.workstreams as WorkstreamBasicWithSummary[],
    });
  }, [
    showsMyCards,
    liveHalfRows,
    liveEnablementRows,
    myNavQuery.data,
    myEnablementNavQuery.data,
    companyNavQuery.data,
    livePillarOrder,
    pillarConfigs,
    liveNarrativeLinks,
    myLdap,
    tf,
  ]);
  const halfCardRows = (personalHalfQuery.data?.workstreams ?? myCardsSnapshot?.myHalf) as
    | WorkstreamBasicWithSummary[]
    | undefined;
  const enablementCardRows = (personalEnablementQuery.data?.workstreams ??
    myCardsSnapshot?.myEnablement) as WorkstreamBasicWithSummary[] | undefined;
  // My workstreams' cards by your role, like the rail; rows the rail hasn't
  // placed fall under Working team.
  const myRoles = useMemo(
    () => new Map([...myNodes, ...myEnablementNodes].map((node) => [node.id, node.myRole])),
    [myNodes, myEnablementNodes]
  );
  // My's cards lead with the updates you owe, then the workstreams with outcomes
  // of yours overdue (where the overdue-outcomes notification lands), each taken
  // out of their role sections. One owing an update stays under Update due.
  const myUpdateDue = useMemo(() => {
    if (personalGroup !== 'mine') return undefined;
    const roleOf = (row: WorkstreamBasicWithSummary) => myRoles.get(row.id);
    const split = (rows: WorkstreamBasicWithSummary[]) => {
      const {due, rest} = splitUpdateDue(rows, roleOf);
      return {due, ...splitOutcomesOverdue(rest, myLdap)};
    };
    const inHalf = split(halfCardRows ?? []);
    const enablement = split(enablementCardRows ?? []);
    // A workstream in both lists shows once.
    const withEnablement = (first: WorkstreamBasicWithSummary[], more: typeof first) => {
      const ids = new Set(first.map((row) => row.id));
      return [...first, ...more.filter((row) => !ids.has(row.id))];
    };
    return {
      due: withEnablement(inHalf.due, enablement.due),
      outcomesOverdue: withEnablement(inHalf.overdue, enablement.overdue),
      half: inHalf.rest,
      enablement: enablement.rest,
    };
  }, [personalGroup, halfCardRows, enablementCardRows, myRoles, myLdap]);
  const myBadgeCount = useMyWorkstreamsBadgeCount({
    timeframe: tf,
    roleOf: (id) => myRoles.get(id),
    myLdap,
  });
  const myPlaceRoots = useMemo(
    () => [placeRow(MY_PLACE_ID, MY_GROUP_LABEL, myBadgeCount || undefined)],
    [myBadgeCount]
  );
  // The Company overview: like My's role sections, one per pillar (the rail's
  // order), labeled with its narrative link, holding its top-level workstreams
  // (the rail's rows under it) in the rail's order.
  const companyRows = activeQuery.data?.workstreams as WorkstreamBasicWithSummary[] | undefined;
  const companySections = useMemo(() => {
    if (!companyOverviewOpen) return undefined;
    const rowsById = new Map((companyRows ?? []).map((row) => [row.id, row]));
    return companyRoots.map((root) => {
      const name = root.node.pillar ?? root.node.name;
      const link = narrativeLinks?.[name];
      const rows = root.children.flatMap((child) => {
        const row = rowsById.get(child.node.id);
        return row ? [row] : [];
      });
      return {
        key: `pillar:${name}`,
        pillar: name,
        action: link && <NarrativeLinkIcon link={link} />,
        workstreams: rows.filter((row) => !isWorkstreamStatusArchived(row)),
        archived: rows.filter(isWorkstreamStatusArchived),
      };
    });
  }, [companyOverviewOpen, companyRows, companyRoots, narrativeLinks]);
  // Each header opens its pillar, like its rail row.
  const linkedCompanySections = companySections?.map((section) => ({
    ...section,
    label: (
      <Link
        to={hrefFor({pillar: section.pillar})}
        state={viaState('section_header')}
        hasNoUnderline
        showUnderlineOnHover
        monochromatic
      >
        {section.pillar}
      </Link>
    ),
  }));
  const scanSections = useMemo(
    () =>
      linkedCompanySections ??
      (personalCards
        ? [
            ...(myUpdateDue
              ? [
                  // Already most overdue first.
                  {
                    key: 'update_due',
                    label: UPDATE_DUE_LABEL,
                    // With Outcomes overdue's, the rail badge's count, so it's
                    // clear where it points.
                    action: myUpdateDue.due.length > 0 && (
                      <AlertCountBadge count={myUpdateDue.due.length} />
                    ),
                    workstreams: myUpdateDue.due,
                  },
                  // Already most overdue first.
                  {
                    key: 'outcomes_overdue',
                    label: OUTCOMES_OVERDUE_LABEL,
                    action: myUpdateDue.outcomesOverdue.length > 0 && (
                      <AlertCountBadge count={myUpdateDue.outcomesOverdue.length} />
                    ),
                    workstreams: myUpdateDue.outcomesOverdue,
                  },
                  ...groupByMyRole(myUpdateDue.half, (row) => myRoles.get(row.id))
                    // With the Exec inbox on, what you sponsor lives there instead.
                    .filter((group) => !(showExec && group.key === 'exec_sponsor'))
                    .map((group) => ({
                      key: group.key,
                      label: group.label,
                      ...toCardSection(group.items),
                    })),
                ]
              : personParam
                ? // A searched person's cards split the same way, by their role.
                  groupByMyRole(halfCardRows ?? [], (row) =>
                    personRole(row.employee_associations, personParam)
                  ).map((group) => ({
                    key: group.key,
                    label: group.label,
                    ...toCardSection(group.items),
                  }))
                : [{key: 'main', ...toCardSection(halfCardRows ?? [])}]),
            {
              key: 'enablement',
              label: ENABLEMENT_GROUP_LABEL,
              tooltip: ENABLEMENT_GROUP_DESCRIPTION,
              ...toCardSection(myUpdateDue?.enablement ?? enablementCardRows ?? []),
            },
            // Empty My: your nearest team's, its header opening the team.
            ...(nearestTeam.team && personalGroup === 'mine' && myIsEmpty
              ? [
                  {
                    key: 'nearest_team',
                    label: nearestTeam.team.slug ? (
                      <Link
                        to={`/teams/${nearestTeam.team.slug}`}
                        hasNoUnderline
                        showUnderlineOnHover
                        monochromatic
                      >
                        {nearestTeam.team.name}
                      </Link>
                    ) : (
                      nearestTeam.team.name
                    ),
                    workstreams: toCardSection(nearestTeam.rows).workstreams,
                  },
                ]
              : []),
          ]
        : undefined),
    [
      linkedCompanySections,
      personalCards,
      myUpdateDue,
      personParam,
      halfCardRows,
      enablementCardRows,
      myRoles,
      showExec,
      nearestTeam.team,
      nearestTeam.rows,
      personalGroup,
      myIsEmpty,
    ]
  );
  const scopeWorkstreams = useMemo<WorkstreamBasicWithSummary[]>(() => {
    if (!personalCards) {
      return (activeQuery.data?.workstreams ?? []) as WorkstreamBasicWithSummary[];
    }
    const rows = [...(halfCardRows ?? []), ...(enablementCardRows ?? [])];
    // Archived rows loaded only for the cards' fold stay out of the rest of the pane.
    return includeArchived
      ? rows
      : rows.filter((workstream) => !isWorkstreamStatusArchived(workstream));
  }, [personalCards, activeQuery.data, halfCardRows, enablementCardRows, includeArchived]);
  const isMetrics = mode === 'metrics';
  // With `?extra=1`, Metrics at Company / pillar level leads with the scorecard.
  const showScorecardInMetrics =
    showExtra && isMetrics && isCompany && !isPersonGroup && selectedId === undefined;

  // Wait for every tree the rail shows before rendering it, so the right group
  // auto-expands on first mount (last visit's trees count).
  const isLoading =
    !landingSnapshot &&
    (myNavQuery.isPending || myEnablementNavQuery.isPending || companyNavQuery.isPending);

  // Whether to show the table (vs detail) in the right pane for Personal mode
  const showPersonalTable = isPersonal && personalGroup !== null && selectedId === undefined;
  // A pillar holding a single workstream opens on it (the URL keeps the pillar).
  // `?table=1` lists it as one row instead, like the old table's pillar scope.
  const pillarSoleChildId = useMemo(() => {
    if (!selectedPillar || !showScanList) return undefined;
    const root = companyRoots.find(
      (item) => (item.node.pillar ?? item.node.name) === selectedPillar
    );
    const only = root?.children.length === 1 ? root.children[0] : undefined;
    return only && !only.node.isPillarGroup ? only.node.id : undefined;
  }, [selectedPillar, companyRoots, showScanList]);
  // The workstream the right pane shows (its card, or its full page), if any.
  const paneWorkstreamId = showPersonalTable ? undefined : (selectedId ?? pillarSoleChildId);

  // Metrics follow the rail too: an open workstream shows its own metrics (its
  // sub-workstreams' expand under it), not the whole scope it sits in.
  // (The card and full page load the same query, so this costs no extra request.)
  const {workstream: paneWorkstream, isLoading: paneWorkstreamLoading} =
    useWorkstream(paneWorkstreamId);
  const paneWorkstreamName =
    paneWorkstream && !isRestrictedWorkstreamAccessResponse(paneWorkstream as never)
      ? paneWorkstream.name
      : undefined;
  // Whether it has sub-workstreams: the rail knows before it loads.
  const paneHasSubs =
    paneWorkstreamId !== undefined &&
    ((allNavNodes.find((node) => node.id === paneWorkstreamId)?.childIds?.length ?? 0) > 0 ||
      (paneWorkstream && !isRestrictedWorkstreamAccessResponse(paneWorkstream as never)
        ? (paneWorkstream.dependencies?.length ?? 0) > 0
        : false));
  // Company's run pillar by pillar in the rail's order, not the list's by-name order.
  const metricsWorkstreams = useMemo<WorkstreamBasicWithSummary[]>(
    () =>
      paneWorkstreamId === undefined
        ? isCompanyScope
          ? sortByRailOrder(scopeWorkstreams, companyRoots)
          : scopeWorkstreams
        : paneWorkstream && !isRestrictedWorkstreamAccessResponse(paneWorkstream as never)
          ? [paneWorkstream]
          : [],
    [paneWorkstreamId, paneWorkstream, scopeWorkstreams, isCompanyScope, companyRoots]
  );
  const metricsControls = useMetricsScorecardControls(metricsWorkstreams, {
    timeframe: metricsTimeframe,
    onChange: setMetricsTimeframe,
  });
  const scopeLoading =
    paneWorkstreamId !== undefined
      ? paneWorkstreamLoading && !paneWorkstream
      : personalCards
        ? personalHalfQuery.isPending || personalEnablementQuery.isPending
        : activeQuery.isPending;

  // The cards' state. My's sections go by your role and Company's by pillar,
  // both read off the rail's trees, so those cards also wait for this
  // timeframe's trees; otherwise they'd land in the wrong sections (or none,
  // "No workstreams here.") and reshuffle once the rail arrives.
  const isNavReady = (query: {isPending: boolean; isPlaceholderData: boolean}) =>
    !query.isPending && !query.isPlaceholderData;
  const cardsWaitOnRail =
    personalGroup === 'mine'
      ? !isNavReady(myNavQuery) || !isNavReady(myEnablementNavQuery)
      : companyOverviewOpen && !isNavReady(companyNavQuery);
  const cardQueries = personalCards ? [personalHalfQuery, personalEnablementQuery] : [activeQuery];
  // Only a failed first load; cards already shown stay through a failed refetch.
  const cardsFailed = cardQueries.some(
    (query) => query.isError && !query.hasFirstPageForCurrentSignature
  );
  // Last visit's cards (landingSnapshot) come with last visit's trees, so they
  // place without either and show at once.
  const cardsLoading =
    !myCardsSnapshot &&
    (cardsWaitOnRail ||
      // An empty My also waits on its nearest team's cards.
      nearestTeam.isLoading ||
      cardQueries.some((query) => !query.hasFirstPageForCurrentSignature));
  // My, a person and Company list labeled sections; a pillar, one run of cards.
  const cardsSkeleton: ScanSkeletonShape =
    personalCards || companyOverviewOpen ? 'sections' : 'list';

  const handleTableSelectCapture = (event: React.MouseEvent<HTMLDivElement>) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    // Buttons inside a row (e.g. the scan list's Update) handle their own clicks.
    if ((event.target as HTMLElement).closest('button')) return;
    const anchor = (event.target as HTMLElement).closest('a');
    // A card's title toggles the card on a plain click; only its tab-opening
    // clicks use the link. The page's own card links go where they say.
    if (anchor?.hasAttribute('data-toggles-card') || anchor?.hasAttribute('data-own-link')) return;
    const href = anchor?.getAttribute('href');
    if (!href) return;
    // A byline's person: scope the page to them.
    const personLdap = parsePersonLink(href);
    if (personLdap) {
      event.preventDefault();
      event.stopPropagation();
      go({person: personLdap}, {via: 'byline_person'});
      return;
    }
    const id = parseDetailLink(href);
    if (id === undefined) return;
    event.preventDefault();
    event.stopPropagation();
    // Links in the right pane (a card's More details, a Part of chip) open the full page.
    // A card's says which section and page it was on.
    const cardOrigin = readScanCardOrigin(event.target as Element);
    go(
      {id, page: 'detail'},
      selectedId !== undefined && page === 'detail'
        ? {via: 'detail_page_link'}
        : {
            via: 'card_link',
            origin: {section: cardOrigin.section, fromPlace: cardOrigin.place},
          }
    );
  };

  // A person scope selects their recent's row (once a link arrival joins recents).
  const scopedPersonRecentIndex = personParam
    ? recentItems.findIndex((item) => item.type === 'person' && item.ldap === personParam)
    : -1;
  const scopedPersonRecent =
    scopedPersonRecentIndex >= 0 ? recentItems[scopedPersonRecentIndex] : undefined;
  const scopedPersonName = scopedPerson?.name ?? scopedPersonRecent?.name;

  // Derive the highlighted group name for the tree's `selectedPillar` prop.
  const activeGroupName = isPersonGroup
    ? null
    : isPersonal
      ? MY_GROUP_LABEL
      : companyOverviewOpen
        ? COMPANY_GROUP_LABEL
        : selectedPillar;

  // In the merged rail a pillar's workstream has no row of its own: the pillar's stands for it.
  const openLeadPillar =
    paneWorkstreamId !== undefined ? pillarLeads.get(paneWorkstreamId) : undefined;
  const navShared: TreeSharedProps = {
    groupSelectionStyle: 'bold',
    // Matches the rail's insets (Layout.Page), so fills run edge to edge.
    rootBleed: RAIL_BLEED,
    // Enablement buckets just show / hide.
    isToggleOnlyGroup: (node) => node.name === ENABLEMENT_GROUP_LABEL,
    // While Audit or Inbox is open (their own rail rows), nothing in the trees is selected.
    selectedId: isPlaceMode(mode) || openLeadPillar ? undefined : paneWorkstreamId,
    selectedPillar: isPlaceMode(mode) ? null : (openLeadPillar ?? activeGroupName),
    narrativeLinks,
    // A rail click only focuses the workstream's card; the card's More details
    // button goes on to the full page. Re-clicking keeps it focused.
    onOpenDetails: (node) => go({id: node.id}, {via: 'rail_workstream'}),
    rowHref: (node) => hrefFor({id: node.id}),
    onOpenGroup: (node) => {
      if (!node.pillar && node.name === MY_GROUP_LABEL) {
        go({}, {via: 'rail_my'});
      } else if (node.id === COMPANY_GROUP_ID) {
        go({company: true}, {via: 'rail_company'});
      } else {
        const name = node.pillar ?? node.name;
        go(
          {pillar: name && name !== ENABLEMENT_GROUP_LABEL ? name : undefined},
          {via: 'rail_pillar'}
        );
      }
    },
  };

  // What's open, for counting visits: a focused workstream (or its page), else
  // a person scope. Each change to it is one visit.
  const visitKey =
    selectedId !== undefined
      ? `workstream:${selectedId}`
      : personParam
        ? `person:${personParam}`
        : null;
  // A search pick records its own visit (with the result's name and photo), so
  // the navigation it triggers doesn't count it again.
  const recordedVisitKey = useRef<string | null>(null);
  const navNodesRef = useRef(allNavNodes);
  navNodesRef.current = allNavNodes;

  const handleSearchSelect = (item: PinnedItem) => {
    recordedVisitKey.current =
      item.type === 'person'
        ? `person:${item.ldap}`
        : item.type === 'workstream'
          ? `workstream:${item.id}`
          : null;
    setRecentItems((prev) => recordVisit(prev, item, Date.now(), MAX_UNPINNED_RECENTS));
    if (item.type === 'person') {
      go({person: item.ldap}, {via: 'search_person'});
    } else if (item.type === 'workstream') {
      go({id: item.id}, {via: 'search_workstream'});
    }
  };

  // Count a visit each time a workstream is focused (or its page opened), or a
  // recent person is scoped to; other people join recents in the effect below.
  useEffect(() => {
    if (visitKey === null) return;
    if (recordedVisitKey.current === visitKey) {
      recordedVisitKey.current = null;
      return;
    }
    const now = Date.now();
    if (selectedId !== undefined) {
      const name =
        navNodesRef.current.find((n) => n.id === selectedId)?.name ??
        paneWorkstreamName ??
        `Workstream #${selectedId}`;
      const item: PinnedItem = {type: 'workstream', id: selectedId, name};
      setRecentItems((prev) => recordVisit(prev, item, now, MAX_UNPINNED_RECENTS));
    } else if (personParam) {
      setRecentItems((prev) => {
        const person = prev.find((p) => p.type === 'person' && p.ldap === personParam);
        return person ? recordVisit(prev, person, now, MAX_UNPINNED_RECENTS) : prev;
      });
    }
    // Only a change of what's open is a visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitKey]);

  // A link straight to a person who isn't a recent adds them (that visit) once
  // their profile loads, so the rail lists and highlights them.
  useEffect(() => {
    if (!personParam || !scopedPerson || !isSameLdap(scopedPerson.ldap, personParam)) return;
    setRecentItems((prev) =>
      prev.some((p) => p.type === 'person' && p.ldap === personParam)
        ? prev
        : recordVisit(
            prev,
            {
              type: 'person',
              ldap: personParam,
              name: scopedPerson.name,
              profilePhoto: scopedPerson.profile_photo_url,
            },
            Date.now(),
            MAX_UNPINNED_RECENTS
          )
    );
  }, [personParam, scopedPerson, setRecentItems]);

  // A link straight to a workstream counts its visit before its name has
  // loaded, so its recent is renamed once the workstream arrives (renames too).
  useEffect(() => {
    if (selectedId === undefined || !paneWorkstreamName) return;
    setRecentItems((prev) =>
      withWorkstreamNames(prev, new Map([[selectedId, paneWorkstreamName]]))
    );
  }, [selectedId, paneWorkstreamName, setRecentItems]);

  const recentForNode = (node: WsNode): PinnedItem | undefined =>
    node.id > 0
      ? recentItems.find((p) => p.type === 'workstream' && p.id === node.id)
      : node.id <= PINNED_BASE_ID
        ? recentItems[PINNED_BASE_ID - node.id]
        : undefined;

  // One pin toggle and one link for a recent: the rail, the strip, and a page's
  // own pin alike.
  const toggleRecentPin = (target: PinnedItem, from: 'rail' | 'strip' | 'detail') => {
    logWorkstreamsInteraction('recent_pinned', {
      pinned: !target.pinned,
      item_type: target.type,
      location: from,
    });
    const now = Date.now();
    setRecentItems((prev) =>
      prev.map((item) => (isSameRecent(item, target) ? togglePin(item, now) : item))
    );
  };
  const recentHref = (item: RecentStripItem) =>
    item.type === 'person' ? hrefFor({person: item.ldap}) : hrefFor({id: item.id});

  const recentsShared: TreeSharedProps = {
    ...navShared,
    // A pillar's workstream keeps its own row here.
    selectedId:
      scopedPersonRecentIndex >= 0 && !isPlaceMode(mode)
        ? PINNED_BASE_ID - scopedPersonRecentIndex
        : isPlaceMode(mode)
          ? undefined
          : paneWorkstreamId,
    onOpenDetails: (node) =>
      node.person
        ? go({person: node.person.ldap}, {via: 'recent_person'})
        : go({id: node.id}, {via: 'recent_workstream'}),
    rowHref: (node) => (node.person ? hrefFor({person: node.person.ldap}) : hrefFor({id: node.id})),
    onPinItem: (node) => {
      const target = recentForNode(node);
      if (target) toggleRecentPin(target, 'rail');
    },
    isItemPinned: (node) => recentForNode(node)?.pinned ?? false,
  };

  // The open workstream's or person's recent (opening it recorded one), for
  // its page's pin.
  const paneRecent = recentItems.find(
    (item) => item.type === 'workstream' && item.id === paneWorkstreamId
  );
  // Held by a placeholder while their profile loads, so the cards don't shift.
  const personHeader =
    scopedPerson || scopedPersonLoading ? (
      <SkeletonFadeIn loading={!scopedPerson} skeleton={<PersonScopeHeaderSkeleton />}>
        {scopedPerson && (
          <PersonScopeHeader
            person={scopedPerson}
            pinned={scopedPersonRecent?.pinned ?? false}
            onTogglePin={
              scopedPersonRecent && (() => toggleRecentPin(scopedPersonRecent, 'detail'))
            }
          />
        )}
      </SkeletonFadeIn>
    ) : null;

  // Roadmap / Scorecards follow the rail: a person, then a workstream, then a
  // pillar, then My workstreams; anything else is the company-wide view.
  const railWorkstreamId = paneWorkstreamId;
  const railWorkstreamPillar =
    railWorkstreamId !== undefined
      ? (allNavNodes.find((node) => node.id === railWorkstreamId)?.pillar ?? null)
      : null;
  const roadmapTarget: RoadmapTarget = isPersonGroup
    ? {kind: 'person', ldap: personalGroup!.slice('person:'.length)}
    : railWorkstreamId !== undefined
      ? {kind: 'workstream', id: railWorkstreamId, pillar: railWorkstreamPillar}
      : isCompany && selectedPillar
        ? {kind: 'pillar', name: selectedPillar}
        : isPersonal && personalGroup === 'mine'
          ? {kind: 'my'}
          : {kind: 'company'};

  // One view event per change of what's open.
  const navigationType = useNavigationType();
  const viewPlace: WorkstreamsViewPlace = isPlaceMode(mode)
    ? mode
    : selectedId !== undefined
      ? 'workstream'
      : personParam
        ? 'person'
        : pillarParam
          ? 'pillar'
          : isCompanyPlace
            ? 'company'
            : 'my';
  // The timeframe isn't part of it: changing it is a setting (timeframe_changed).
  const viewKey = [mode, place, page, selectedId, personParam, pillarParam].join('|');
  useEffect(() => {
    const isPop = navigationType === 'POP';
    const backVia = isPop && pendingBackVia.current;
    pendingBackVia.current = false;
    const shownRecents = pickVisibleRecents(recentItems, Date.now());
    logWorkstreamsViewed({
      place: viewPlace,
      page: selectedId === undefined ? 'list' : page,
      mode: isPlaceMode(mode) ? undefined : mode,
      workstreamId: selectedId,
      via: backVia ? 'back' : isPop ? 'history_or_load' : (readVia(location.state) ?? 'link'),
      origin: isPop ? undefined : readViaOrigin(location.state),
      timeframe: isMetrics ? toMetricsTimeframeParam(metricsTimeframe) : half,
      recents: {
        shown: shownRecents.length,
        pinned: shownRecents.filter((item) => item.pinned).length,
      },
    });
    // Only a change of what's open is a view (not a re-render, nor a re-click).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewKey]);

  // Every Update button on the page (cards, Inbox, the detail page) opens the one modal.
  const openUpdate = (id: number, from?: UpdateOpenedFrom) => {
    logWorkstreamsInteraction('update_opened', {workstream_id: id, ...from});
    setUpdateId(id);
  };
  // A new (already named) workstream opens on its full page, and the rail and
  // My cards reload to include it.
  const handleWorkstreamCreated = (id: number) => {
    logWorkstreamsInteraction('workstream_created', {workstream_id: id});
    navigate(hrefFor({id, page: 'detail'}), {
      state: {source: 'create', workstreamId: id, ...viaState('create')},
    });
    void myNavQuery.refetch();
    void myEnablementNavQuery.refetch();
    if (showScanList) {
      void personalHalfQuery.refetch();
      void personalEnablementQuery.refetch();
    } else {
      void personalQuery.refetch();
    }
  };

  // `?table=1` has no cards, so a focused workstream opens on its full page.
  const showFocusCards = page === 'focus' && showScanList;

  const [auditScope, setAuditScope] = useState<AuditScope | null>(null);
  // Metrics keeps its timeframe in the table's column header instead.
  const modeControls =
    mode === 'execution' || mode === 'wireframes' ? (
      <TimeframeDropdown
        value={half}
        onChange={(next) => {
          logWorkstreamsInteraction('timeframe_changed', {timeframe: next});
          setHalf(next);
        }}
      />
    ) : null;
  // Details | Metrics don't apply to Audit, so its own switch takes their place;
  // Inbox has none.
  const modeSwitch =
    mode === 'audit' ? (
      <AuditScopeSwitch
        requested={auditScope}
        onChange={(next) => {
          logWorkstreamsInteraction('audit_scope_changed', {scope: next});
          setAuditScope(next);
        }}
      />
    ) : mode === 'inbox' ? null : (
      <SegmentedControl
        selectedValue={mode}
        onSelection={(value) => {
          if (!value) return;
          // Wireframes open on Company unless the rail already picked a pillar or workstream.
          const keepsScope = value !== 'wireframes' || selectedPillar || paneWorkstreamId;
          go(keepsScope ? {...here, mode: value as Mode} : {mode: 'wireframes', company: true}, {
            via: 'mode_switch',
          });
        }}
      >
        <SegmentedControl.Option variant="text" value="execution">
          Details
        </SegmentedControl.Option>
        <SegmentedControl.Option variant="text" value="metrics">
          Metrics
        </SegmentedControl.Option>
        {canViewWireframes && (
          <SegmentedControl.Option variant="text" value="wireframes">
            Wireframes
          </SegmentedControl.Option>
        )}
        {showExtra && (
          <SegmentedControl.Option variant="text" value="roadmap">
            Roadmap
          </SegmentedControl.Option>
        )}
      </SegmentedControl>
    );

  // The rail, in three bands: places (a flat My workstreams, Exec inbox, Audit, Company),
  // the pillars, then search and recents. A column beside the pane, or (narrow
  // screens) the header's sheet, with finger-sized rows.
  // Places fill when selected, like Audit; pillars below keep the open group's tint.
  const placeShared = (touch: boolean, bleed: {left: number; right: number}): TreeSharedProps => ({
    ...navShared,
    touch,
    rootBleed: bleed,
    groupSelectionStyle: 'fill',
    disableRootTint: true,
  });
  const renderNav = ({touch}: {touch: boolean}) => {
    // Rows bleed back across whichever container they're in.
    const bleed = touch ? SHEET_BLEED : RAIL_BLEED;
    return (
      <>
        {/* While loading, the tree is a skeleton and the search stays live;
        recents wait, since they dedupe against the loaded tree. */}
        <SkeletonFadeIn
          loading={isLoading}
          skeleton={<NavTreeSkeleton divider={<RailDivider bleed={bleed} />} />}
        >
          <WorkstreamTree roots={myPlaceRoots} shared={placeShared(touch, bleed)} />
          {showExec && (
            <InboxRailRow
              selected={mode === 'inbox'}
              onSelect={() => go({mode: 'inbox'}, {via: 'rail_inbox'})}
              bleed={bleed}
              touch={touch}
            />
          )}
          <AuditRailRow
            selected={mode === 'audit'}
            onSelect={() => go({mode: 'audit'}, {via: 'rail_audit'})}
            bleed={bleed}
            touch={touch}
          />
          <WorkstreamTree roots={COMPANY_PLACE_ROOTS} shared={placeShared(touch, bleed)} />
          <RailDivider bleed={bleed} />
          {companyRoots.length === 0 ? (
            <EmptyState
              title="No workstreams yet"
              detail="Nothing here for this view. Try another timeframe."
            />
          ) : (
            <WorkstreamTree
              roots={railCompanyRoots}
              shared={{
                ...navShared,
                touch,
                rootBleed: bleed,
                reserveChevronSlot: true,
              }}
            />
          )}
        </SkeletonFadeIn>
        <RailDivider bleed={bleed} />
        <Box display="flex" alignItems="center">
          <WorkstreamsSearch onPin={handleSearchSelect} bleed={bleed} touch={touch} />
        </Box>
        <SkeletonFadeIn loading={isLoading} skeleton={null}>
          {visibleRecentRoots.length > 0 && (
            <Box paddingTop="8">
              <WorkstreamTree
                roots={visibleRecentRoots}
                shared={{...recentsShared, touch, rootBleed: bleed}}
              />
            </Box>
          )}
        </SkeletonFadeIn>
      </>
    );
  };

  // What the narrow screens' sheet button and the tab read: the open workstream,
  // else the selected group (My, a pillar, a person), else the whole company.
  const scopeName =
    mode === 'audit'
      ? 'Audit'
      : mode === 'inbox'
        ? 'Exec inbox'
        : // A pillar opened on its one workstream is still the pillar.
          selectedPillar
          ? selectedPillar
          : paneWorkstreamId !== undefined
            ? (allNavNodes.find((node) => node.id === paneWorkstreamId)?.name ?? paneWorkstreamName)
            : isPersonGroup
              ? // Before a linked-to person's profile loads (not in recents yet), their ldap.
                (scopedPersonName ?? personParam)
              : (activeGroupName ?? 'All workstreams');
  const scopeLabel = scopeName ?? 'Workstream';
  useDocumentTitle(scopeName ?? 'Workstreams');

  return (
    <>
      <Layout.Page
        header={
          <Layout.PageHeader
            // The title is home: My workstreams, in the current mode.
            title="Workstreams"
            titleTo={hrefFor({})}
            titleAccessory={
              isMobile ? (
                // Right after the title, so it reads "Workstreams › <scope>".
                <Box display="flex" alignItems="center" marginLeft="12" style={{minWidth: 0}}>
                  <MobileNavSheet label={scopeLabel}>{renderNav({touch: true})}</MobileNavSheet>
                </Box>
              ) : undefined
            }
            create={<NewWorkstreamButton onCreated={handleWorkstreamCreated} />}
            // Mode, then its controls. (Who's here sits on the workstream page's
            // title row.)
            toolbar={
              <>
                {modeSwitch}
                {modeControls}
              </>
            }
          />
        }
        // Narrow screens open the rail as a sheet from the header instead.
        rail={isMobile ? undefined : renderNav({touch: false})}
      >
        {mode === 'audit' ? (
          <Box style={{flex: 1, minWidth: 0, overflow: 'auto'}}>
            <AuditPane requested={auditScope} />
          </Box>
        ) : mode === 'inbox' ? (
          <Box
            style={{flex: 1, minWidth: 0, overflow: 'auto'}}
            // The cards' title and More details links open the full page here.
            onClickCapture={handleTableSelectCapture}
          >
            <InboxPane onLeaveUpdate={openUpdate} />
          </Box>
        ) : mode === 'wireframes' ? (
          <Box style={{flex: 1, minWidth: 0, overflow: 'auto'}}>
            <WireframesPane
              timeframe={half}
              // A focused workstream shows its pillar.
              pillarName={selectedPillar ?? railWorkstreamPillar}
              onSelectPillar={(name) =>
                go(name ? {pillar: name} : {company: true}, {via: 'wireframes_tab'})
              }
              hrefForWorkstream={(id) => hrefFor({mode: 'execution', id})}
            />
          </Box>
        ) : mode === 'roadmap' ? (
          <Box style={{flex: 1, minWidth: 0, minHeight: 0, overflow: 'hidden'}}>
            <EmbeddedRoadmap target={roadmapTarget} />
          </Box>
        ) : isMetrics ? (
          <Layout.MaxWidth
            // The cards' cap, so the table doesn't stretch on huge screens.
            width="wide"
            style={{flex: 1, minHeight: 0, overflow: 'auto'}}
          >
            {showScorecardInMetrics && <EmbeddedScorecards pillar={selectedPillar} />}
            {personHeader && selectedId === undefined && (
              // On the table's 32px sides (its frame's 16px + the scorecard's own).
              <Box paddingX="32" paddingTop="24">
                {personHeader}
              </Box>
            )}
            {scopeLoading ? (
              <Box display="flex" justifyContent="center" padding="24">
                <Spinner />
              </Box>
            ) : (
              // The cards' frame (WorkstreamScanList): same width cap; with the
              // scorecard's own 16px inset, its table lands on the cards' 32px
              // sides and 24px top.
              <Box paddingX="16" paddingTop="8" paddingBottom="16" style={{height: '100%'}}>
                <Layout.MaxWidth width="wide" style={{height: '100%'}}>
                  <WorkstreamMetricsScorecardList
                    workstreams={metricsWorkstreams}
                    controls={metricsControls}
                    timeframeInHeader
                    v4Analytics
                    scopeName={scopeName}
                  />
                </Layout.MaxWidth>
              </Box>
            )}
          </Layout.MaxWidth>
        ) : paneWorkstreamId !== undefined ? (
          <Box
            // Remounts per workstream / page, so each starts scrolled to the top.
            key={`${paneWorkstreamId}-${page}`}
            onClickCapture={handleTableSelectCapture}
            style={{flex: 1, minWidth: 0, minHeight: 0, overflow: 'auto'}}
          >
            {showFocusCards && paneHasSubs ? (
              <ParentWorkstreamView workstreamId={paneWorkstreamId} onLeaveUpdate={openUpdate} />
            ) : showFocusCards ? (
              <WorkstreamFocus workstreamId={paneWorkstreamId} onLeaveUpdate={openUpdate} />
            ) : (
              <WorkstreamDetailView
                workstreamId={paneWorkstreamId}
                subworkstreamsAsTable={!showScanList}
                onLeaveUpdate={openUpdate}
                onClose={() => closeDetail(paneWorkstreamId)}
                pinned={paneRecent?.pinned ?? false}
                onTogglePin={() => paneRecent && toggleRecentPin(paneRecent, 'detail')}
              />
            )}
          </Box>
        ) : (
          <Box
            display="flex"
            flexDirection="column"
            onClickCapture={handleTableSelectCapture}
            // The table's 8px top margin + 1px top border tuck under the header
            // line (-9px), and it scrolls itself; the scan list has neither.
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              overflow: showScanList ? 'auto' : 'hidden',
              marginTop: showScanList ? 0 : -9,
            }}
          >
            {showScanList && personHeader && (
              // On the cards' edge; their own 24px top spaces the first section.
              <Box paddingTop="24">
                <Layout.MaxWidth width="reading" inset>
                  {personHeader}
                </Layout.MaxWidth>
              </Box>
            )}
            {showScanList &&
              personalGroup === 'mine' &&
              pickRecentItems(recentItems).length > 0 && (
                // Lined up with the cards below it (the strip sets its own inset);
                // skipped with no recents so its padding doesn't stack on the cards'.
                <Box paddingTop="24">
                  <RecentsStrip
                    items={recentItems}
                    hrefFor={recentHref}
                    onTogglePin={(item) => toggleRecentPin(item, 'strip')}
                    onNamesLoaded={(names) =>
                      setRecentItems((prev) => withWorkstreamNames(prev, names))
                    }
                  />
                </Box>
              )}
            {showScanList &&
              !cardsFailed &&
              personalGroup === 'mine' &&
              myIsEmpty &&
              !nearestTeam.isLoading && (
                <MyEmptyIntro
                  halfLabel={formatWorkstreamTimeframeLabel(half)}
                  // A status filter can hide all of the team's; then point at Company.
                  teamName={nearestTeam.rows.length > 0 ? nearestTeam.team?.name : null}
                  teamCards={nearestTeam.rows.length}
                  companyHref={hrefFor({company: true})}
                />
              )}
            {cardsFailed ? (
              <LoadError
                message={t('error_loading_workstreams')}
                onRetry={() => cardQueries.forEach((query) => void query.refetch())}
              />
            ) : showScanList ? (
              // Top-set cards flag a missing north star.
              <ScanCoreWorkstreamsContext.Provider value={coreWorkstreamIds}>
                <WorkstreamScanList
                  workstreams={scopeWorkstreams}
                  sections={scanSections}
                  loading={cardsLoading}
                  skeleton={cardsSkeleton}
                  onLeaveUpdate={openUpdate}
                  hideEmptyText={personalGroup === 'mine' && myIsEmpty}
                  place={viewPlace}
                />
              </ScanCoreWorkstreamsContext.Provider>
            ) : (
              <WorkstreamListTable
                workstreams={scopeWorkstreams}
                loading={!activeQuery.hasFirstPageForCurrentSignature}
                place={viewPlace}
                onLeaveUpdate={openUpdate}
                filter={{
                  statusFilter,
                  includeArchived,
                  onStatusFilterChange: setStatusFilter,
                  onIncludeArchivedChange: setIncludeArchived,
                }}
              />
            )}
          </Box>
        )}
      </Layout.Page>

      <WorkstreamStatusUpdateModal
        workstreamId={updateId}
        onClose={() => setUpdateId(undefined)}
        source="prototype-tree"
        onSubmitted={() => {
          if (showScanList) {
            void personalHalfQuery.refetch();
            void personalEnablementQuery.refetch();
          } else {
            void personalQuery.refetch();
          }
          // (refetch() runs even when disabled.)
          if (companyListShown) void companyWorkstreamsQuery.refetch();
          void myNavQuery.refetch();
          void companyNavQuery.refetch();
        }}
      />
    </>
  );
};
